import { useState } from "react";
import { Undo2 } from "lucide-react";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { credits, rupees } from "../products/types";
import { RefundStatusBadge } from "../refunds/refund-status-badge";
import { formatWhen } from "./format-when";
import { useOrderRefund, useRequestRefund } from "./queries";
import type { OrderDetails, RefundRequest } from "./types";

const REASON_MAX = 1000;

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

/** Orders whose unused credits can still be refunded. */
const refundable = (order: OrderDetails) =>
  order.credits > 0 && (order.status === "payment_successful" || order.status === "partially_refunded");

/**
 * On a paid plan order: what a refund of its unused credits would return now, the
 * account's requests, and the way to ask (a TechLadder admin reviews each request).
 */
export function RefundRequestSection({ order }: { order: OrderDetails }) {
  const show = refundable(order) || order.refunded_amount > 0;
  const refund = useOrderRefund(order.id, show);
  const ask = useRequestRefund(order.id);
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  if (!show) return null;

  const requests = refund.data?.requests ?? [];
  const open = requests.find((r) => r.status === "requested" || r.status === "processing");
  const quote = refund.data?.quote;
  const canAsk = refundable(order) && !open && Boolean(quote && quote.credits > 0);
  const reasonError = reason.trim() ? null : "Say why you want a refund.";

  const submit = () => {
    setTouched(true);
    if (reasonError || ask.isPending) return;
    ask.mutate(reason.trim(), {
      onSuccess: () => {
        toast.success("Refund requested", "We will review it shortly.");
        setReason("");
        setTouched(false);
      },
      onError: (err) => toast.danger("Could not request a refund", errorMessage(err, "Try again.")),
    });
  };

  return (
    <section className="space-y-4 rounded-2xl border border-line bg-elevated p-6 shadow-sm" aria-label="Refund">
      <div className="flex items-center gap-2">
        <Undo2 className="h-5 w-5 text-ink-3" />
        <h2 className="text-lg font-semibold text-ink">Refund</h2>
      </div>

      {refund.isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-soft" />
      ) : refund.isError ? (
        <p className="text-sm text-danger">{errorMessage(refund.error, "Could not load refund details.")}</p>
      ) : (
        <>
          {quote && refundable(order) && (
            <p className="text-sm text-ink-2">
              {quote.credits > 0 ? (
                <>
                  Only unused credits are refunded. Right now that is{" "}
                  <span className="font-semibold tabular-nums text-ink">{credits(quote.credits)} credits</span> ={" "}
                  <span className="font-semibold tabular-nums text-ink">{rupees(quote.total)}</span> (incl.{" "}
                  {rupees(quote.gst_amount)} GST). The final amount is worked out when your request is approved.
                </>
              ) : (
                "All credits from this order have been used or refunded, so there is nothing to refund."
              )}
            </p>
          )}

          {requests.length > 0 && (
            <ul className="space-y-3" aria-label="Refund requests">
              {requests.map((r) => (
                <RequestRow key={r.id} request={r} />
              ))}
            </ul>
          )}

          {canAsk && (
            <div className="space-y-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-ink">Why do you want a refund?</span>
                <textarea
                  value={reason}
                  rows={3}
                  maxLength={REASON_MAX}
                  onChange={(e) => setReason(e.target.value)}
                  aria-label="Refund reason"
                  className={cn(
                    "w-full resize-y rounded-lg border px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/30",
                    touched && reasonError ? "border-danger/50" : "border-line focus:border-brand",
                  )}
                />
              </label>
              {touched && reasonError && <p className="text-xs text-danger">{reasonError}</p>}
              <button
                onClick={submit}
                disabled={ask.isPending}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-line px-4 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-wait disabled:opacity-60"
              >
                <Undo2 className="h-4 w-4" /> {ask.isPending ? "Requesting…" : "Request refund"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RequestRow({ request }: { request: RefundRequest }) {
  return (
    <li className="rounded-xl border border-line p-4 text-sm" aria-label={`Refund request ${request.status}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-3">Requested {formatWhen(request.created_at)}</span>
        <RefundStatusBadge status={request.status} />
      </div>
      <p className="mt-2 text-ink">{request.reason}</p>
      {request.total_amount !== null && request.credits !== null && (
        <p className="mt-1 tabular-nums text-ink-2">
          {rupees(request.total_amount)} for {credits(request.credits)} unused credits
          {request.refund_destination &&
            (request.status === "refunded"
              ? ` · sent back to ${request.refund_destination}`
              : request.status === "processing"
                ? ` · going back to ${request.refund_destination}`
                : "")}
        </p>
      )}
      {request.status === "processing" && (
        <p className="mt-1 text-ink-3">Banks usually take 5–7 working days to show a refund.</p>
      )}
      {request.status === "rejected" && request.review_note && (
        <p className="mt-1 text-ink-2">Reason: {request.review_note}</p>
      )}
      {request.status === "failed" && (
        <p className="mt-1 text-danger">The refund did not go through; your credits were given back.</p>
      )}
    </li>
  );
}
